import { CognitoUser, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { LoadingPage } from 'src/components/loading-page';
import { useCatchError } from 'src/hooks/use-catch-error';
import { assertContext } from 'src/utils/assert-context';
import { refreshCognitoToken } from 'src/utils/axios-instance';
import * as cognito from 'src/utils/cognito';
import { Unauthorized } from 'src/utils/errors';
import { isObject } from 'src/utils/is-object';
import { useMemoOne } from 'use-memo-one';

export type Cognito = typeof cognito;

const CognitoContext = createContext(cognito);

// just for mocking
export const CognitoProvider = CognitoContext.Provider;

export function useCognito() {
  return useContext(CognitoContext);
}

type CognitoUserContextType = {
  data: CognitoUser | null;
  refreshData: () => void;
};

// pass undefined as any, because we run assertContext at runtime
const CognitoUserContext = createContext<CognitoUserContextType>(
  undefined as any
);

export function useUserAttributes() {
  const catchError = useCatchError();
  const { getSession } = useCognito();
  const { data: user } = useCognitoUser();

  const [loading, setLoading] = useState(false);
  const [userAttributes, setUserAttributes] = useState<CognitoUserAttribute[]>(
    []
  );

  useEffect(() => {
    if (!user) return;

    const getUserData = async () => {
      setLoading(true);

      try {
        const session = await getSession(user);

        if (!session.isValid()) {
          throw new Unauthorized();
        }

        user.getUserAttributes((_err, result) => {
          setUserAttributes(result || []);
          setLoading(false);
        });
      } catch (error) {
        if (isObject(error) && error.code === 'NotAuthorizedException') {
          catchError(new Unauthorized());
        } else {
          catchError(error);
        }
      }
    };

    getUserData();
  }, [user, getSession, catchError]);

  return { user, loading, userAttributes };
}

export const useCognitoUser = () => {
  const context = useContext(CognitoUserContext);
  assertContext(context, 'CognitoUser');
  return context;
};

export const CognitoUserProvider: FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback = <LoadingPage /> }) => {
  const cognito = useCognito();

  // `undefined` means loading
  const [data, setUser] = useState<CognitoUser | null | undefined>();

  const refreshData = useCallback(() => {
    setUser(cognito.getCurrentUser());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemoOne<CognitoUserContextType | undefined>(
    () => (data === undefined ? undefined : { data, refreshData }),
    [data, refreshData]
  );

  useEffect(() => {
    refreshCognitoToken(cognito).then(() => setUser(cognito.getCurrentUser()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (value === undefined) return <>{fallback}</>;

  return (
    <CognitoUserContext.Provider value={value}>
      {children}
    </CognitoUserContext.Provider>
  );
};
